// Users API Service
import apiClient from './index';
import axios from 'axios';
import { API_BASE_URL, getToken } from './config';

/**
 * Get user by ID
 * @param {number} userId - User ID
 * @returns {Promise} API response with user data
 */
export const getUserById = async (userId) => {
  try {
    const response = await apiClient.get(`/users/${userId}`);

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'User retrieved successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to retrieve user',
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Failed to retrieve user',
      error: error.error || error.message,
    };
  }
};

/**
 * Update user
 * @param {number} userId - User ID
 * @param {object} userData - User data to update
 * @returns {Promise} API response with updated user data
 */
export const updateUser = async (userId, userData) => {
  try {
    const response = await apiClient.put(`/users/${userId}`, userData);

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'User updated successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to update user',
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Failed to update user',
      error: error.error || error.message,
      errors: error.errors || {},
    };
  }
};

/**
 * Get user dashboard statistics
 * @param {number} userId - User ID
 * @param {object} options - Query options (start_date, end_date)
 * @returns {Promise} API response with dashboard data
 */
export const getUserDashboard = async (userId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.start_date) params.append('start_date', options.start_date);
    if (options.end_date) params.append('end_date', options.end_date);

    const queryString = params.toString();
    const url = `/users/${userId}/dashboard${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Dashboard statistics retrieved successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to retrieve dashboard statistics',
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Failed to retrieve dashboard statistics',
      error: error.error || error.message,
    };
  }
};

/**
 * Upload profile image
 * @param {File} imageFile - Image file to upload
 * @returns {Promise} API response with image URL
 */
export const uploadProfileImage = async (imageFile) => {
  try {
    const formData = new FormData();
    formData.append('image', imageFile);

    const token = getToken();
    
    const response = await axios.post(`${API_BASE_URL}/profile/upload-image`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    // Handle API response: { status: true, message: "...", url: "https://..." }
    if (response.data && (response.data.status || response.data.success || response.data.url)) {
      const imageUrl = response.data.url || response.data.image_url || response.data.data?.url;
      
      return {
        success: true,
        imageUrl: imageUrl, // Full URL to send to backend
        message: response.data.message || 'Profile image uploaded successfully',
      };
    }

    return {
      success: false,
      message: response.data?.message || 'Failed to upload profile image',
    };
  } catch (error) {
    console.error('Error uploading profile image:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to upload profile image',
      error: error.response?.data?.error || error.message,
    };
  }
};

